import { useCreateReviewMutation } from '@/client/graphql/reviews.generated';
import { CreateReviewInput } from '@/client/graphql/types.generated';
import {
  FormControlErrorMap,
  setFormControlErrors,
  UncaughtFormControlError,
} from '@/utils/formControlErrors';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ApplicationError } from '@meritcircle/types';
import { Rating } from '@meritcircle/ui/components';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useTranslatedRatingLabels } from '../useTranslatedRatingLabels';

type ModalContentFormProps = {
  gameSlug: string;
  gameTitle: string;
  onClose: () => void;
  onSuccess: () => void;
};

type Values = Partial<Pick<CreateReviewInput, 'review'>>;

export const ModalContentForm = ({
  gameSlug,
  gameTitle,
  onClose,
  onSuccess,
}: ModalContentFormProps) => {
  const [, mutate] = useCreateReviewMutation();

  const { t } = useTranslation('reviews');
  const tooltipLabels = useTranslatedRatingLabels();

  const [rating, setRating] = useState(0);

  const errorMap: FormControlErrorMap<Values> = {
    [ApplicationError.GAME_NOT_FOUND]: {
      field: 'review',
      value: t('modal.errors.game-not-found'),
    },
    [ApplicationError.ALREADY_REVIEWED]: {
      field: 'review',
      value: t('modal.errors.already-reviewed'),
    },
  };

  const onSubmit = async (input: Values) => {
    const result = await mutate({ createReviewInput: { gameSlug, rating, review: input.review } });

    if (result.error) {
      return setFormControlErrors(result.error, errorMap, setError, clearErrors);
    }
    setRating(0);
    onSuccess();
  };

  const handleClose = () => {
    setRating(0);
    onClose();
  };

  const ValidationSchema = Yup.object().shape({
    review: Yup.string()
      .typeError(t('modal.errors.review-required'))
      .required(t('modal.errors.review-required')),
  });

  const {
    handleSubmit,
    register,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: yupResolver(ValidationSchema),
    defaultValues: {
      review: '',
    },
  });

  return (
    <ModalContent display="flex" gap="space.24">
      <ModalHeader pb={0}>
        {t('modal.create-review.title')} <span>{gameTitle}</span>?
      </ModalHeader>
      <ModalCloseButton color="muted" />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <ModalBody>
          <Rating
            rating={rating}
            interactive={true}
            handleStarClick={setRating}
            size="lg"
            showOnlyStars={true}
            tooltipLabels={tooltipLabels}
          />
          {!!rating && (
            <FormControl variant="review" mt="md" isRequired isInvalid={!!errors.review}>
              <Textarea {...register('review')} rows={5} />
              <FormLabel>{t('modal.create-review.label')}</FormLabel>
              <FormErrorMessage data-testid="error-message">
                {errors?.review?.message}
              </FormErrorMessage>
            </FormControl>
          )}
          <UncaughtFormControlError errors={errors} mt="sm" />
        </ModalBody>

        <ModalFooter flexDirection="row" justifyContent="flex-end" gap="space.20">
          {!rating ? (
            <Button onClick={handleClose} variant="outline">
              {t('modal.create-review.close-button')}
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={handleClose}>
                {t('modal.create-review.close-button')}
              </Button>
              <Button variant="primary" type="submit" isLoading={isSubmitting}>
                {t('modal.create-review.submit-button')}
              </Button>
            </>
          )}
        </ModalFooter>
      </form>
    </ModalContent>
  );
};
